import { Box, Button, Checkbox, FormControlLabel } from "@mui/material";
import { exportToCsv } from "@/src/utils/export";
import {
  actionSx,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";

interface ResultsSummaryProps {
  sortedAndFilteredArticles: any[];
  paginatedArticles: any[];
  selectedArticles: any[];
  setSelectedArticles: any;
  selectAllState: any;
  isAllResultsSelected: any;
  isSomeOnPageSelected: any;
  handleSelectAllOnPage: any;
  totalResults: number;
  loading: boolean;
}

const countSx = {
  fontFamily: FONT_UI,
  fontSize: "0.8125rem",
  color: "custom.inkMuted",
};

const linkButtonSx = {
  border: 0,
  p: 0,
  background: "none",
  cursor: "pointer",
  fontFamily: FONT_UI,
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color: "primary.main",
  textDecoration: "underline",
  textUnderlineOffset: "3px",
};

const SearchResultsSummary = ({
  sortedAndFilteredArticles,
  paginatedArticles,
  selectedArticles,
  setSelectedArticles,
  selectAllState,
  isAllResultsSelected,
  isSomeOnPageSelected,
  handleSelectAllOnPage,
  totalResults,
}: ResultsSummaryProps) => {
  const handleSelectAllResults = () => {
    setSelectedArticles(sortedAndFilteredArticles.map((a) => a.doi));
  };

  const handleClearSelection = () => {
    setSelectedArticles([]);
  };

  const handleBulkExport = () => {
    exportToCsv(
      sortedAndFilteredArticles.filter((article) =>
        selectedArticles.includes(article.doi),
      ),
    );
  };

  return (
    <Box
      sx={{
        py: { xs: 0.75, md: 1 },
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        columnGap: 1.5,
        rowGap: 0.5,
        ...hairline,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {paginatedArticles.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selectAllState === true}
                indeterminate={selectAllState === "indeterminate"}
                onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                sx={{ py: 0.25 }}
              />
            }
            label="Select page"
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": {
                fontFamily: FONT_UI,
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "custom.ink",
              },
            }}
          />
        )}
        <Box component="span" sx={countSx}>
          {totalResults > 0
            ? `${paginatedArticles.length} of ${totalResults} results`
            : "No articles match this query"}
        </Box>
      </Box>

      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
      >
        {isAllResultsSelected ? (
          <>
            <Box component="span" sx={countSx}>
              All {totalResults} selected
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleClearSelection}
              sx={linkButtonSx}
            >
              Clear
            </Box>
          </>
        ) : isSomeOnPageSelected ? (
          <Box
            component="button"
            type="button"
            onClick={handleSelectAllResults}
            sx={linkButtonSx}
          >
            Select all {totalResults}
          </Box>
        ) : null}

        {selectedArticles.length > 0 && (
          <>
            <Box component="span" sx={countSx}>
              {selectedArticles.length} selected
            </Box>
            <Button
              type="button"
              onClick={handleBulkExport}
              sx={actionSx("solid", "small")}
            >
              Export CSV
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SearchResultsSummary;
